/**
 * c27-mobile-viewport.js
 * PDP 移动端视口协调：stable vh（文档流/滚动轨道）与 visual vh（fixed 舞台）分离。
 *
 * - 地址栏纯高度变化：仅 height-tick（requestTick），不更新 stable vh、不改 scrollTop、不 rebuild geometry
 * - 宽度变化 ≥60px / orientationchange / 断点切换：geometry-rebuild（可更新 stable vh、允许 section 完整 reset）
 */
(function (w) {
  'use strict';

  var MOBILE_BP = 768;
  var WIDTH_REBUILD_PX = 60;
  var GEOMETRY_DEBOUNCE_MS = 150;

  var stableVh = 0;
  var lastStableWidth = 0;
  var wasMobile = null;
  var geometryTimer = null;
  var heightTickCallbacks = [];
  var geometryRebuildCallbacks = [];

  function isMobile() {
    return (w.innerWidth || 0) < MOBILE_BP;
  }

  /** 文档流滚动轨道：首次加载或 geometry rebuild 时取自 clientHeight，不用 innerHeight / visualViewport */
  function readStableVhFromDoc() {
    var docH = document.documentElement.clientHeight || 0;
    if (docH > 0) return Math.round(docH);
    return Math.round(w.innerHeight || 0);
  }

  /** fixed 舞台可视高度：可用 visualViewport.height */
  function getVisualVh() {
    var vv = w.visualViewport;
    if (vv && vv.height > 0) return Math.round(vv.height);
    return w.innerHeight || readStableVhFromDoc();
  }

  function refreshStableVh() {
    stableVh = readStableVhFromDoc();
    lastStableWidth = w.innerWidth || 0;
    wasMobile = isMobile();
    document.documentElement.style.setProperty('--c27-stable-vh', stableVh + 'px');
  }

  function getStableVh() {
    if (stableVh < 1) refreshStableVh();
    return stableVh;
  }

  function dispatchHeightTick() {
    var detail = { stableVh: getStableVh(), visualVh: getVisualVh() };
    heightTickCallbacks.forEach(function (fn) {
      try {
        fn(detail);
      } catch (e) { /* ignore */ }
    });
    w.dispatchEvent(new CustomEvent('c27:viewport-height-tick', { detail: detail }));
  }

  function runGeometryRebuild(reason) {
    refreshStableVh();
    var detail = { reason: reason, stableVh: stableVh, visualVh: getVisualVh() };
    w.requestAnimationFrame(function () {
      w.requestAnimationFrame(function () {
        geometryRebuildCallbacks.forEach(function (fn) {
          try {
            fn(detail);
          } catch (e) { /* ignore */ }
        });
        w.dispatchEvent(new CustomEvent('c27:viewport-geometry-rebuild', { detail: detail }));
      });
    });
  }

  function scheduleGeometryRebuild(reason) {
    if (geometryTimer) w.clearTimeout(geometryTimer);
    geometryTimer = w.setTimeout(function () {
      geometryTimer = null;
      runGeometryRebuild(reason);
    }, GEOMETRY_DEBOUNCE_MS);
  }

  function shouldRebuildGeometry(widthDelta, reason) {
    if (reason === 'orientationchange') return true;
    if (widthDelta >= WIDTH_REBUILD_PX) return true;
    if (wasMobile !== isMobile()) return true;
    return false;
  }

  function handleLayoutSignal(reason, widthHint) {
    if (!isMobile()) {
      refreshStableVh();
      return;
    }

    var wdt = widthHint != null ? widthHint : w.innerWidth || 0;
    var widthDelta = Math.abs(wdt - lastStableWidth);

    if (shouldRebuildGeometry(widthDelta, reason)) {
      scheduleGeometryRebuild(reason);
      return;
    }

    dispatchHeightTick();
  }

  function subscribe(list, fn) {
    list.push(fn);
    return function unsubscribe() {
      var idx = list.indexOf(fn);
      if (idx >= 0) list.splice(idx, 1);
    };
  }

  refreshStableVh();

  w.addEventListener(
    'resize',
    function () {
      handleLayoutSignal('resize');
    },
    { passive: true }
  );

  w.addEventListener(
    'orientationchange',
    function () {
      scheduleGeometryRebuild('orientationchange');
    },
    { passive: true }
  );

  if (w.visualViewport) {
    w.visualViewport.addEventListener(
      'resize',
      function () {
        if (!isMobile()) return;
        var wdt = w.innerWidth || 0;
        var widthDelta = Math.abs(wdt - lastStableWidth);
        if (widthDelta >= WIDTH_REBUILD_PX) {
          scheduleGeometryRebuild('visualViewport-width');
        } else {
          dispatchHeightTick();
        }
      },
      { passive: true }
    );
  }

  w.C27MobileViewport = {
    MOBILE_BP: MOBILE_BP,
    WIDTH_REBUILD_PX: WIDTH_REBUILD_PX,
    isMobile: isMobile,
    getStableVh: getStableVh,
    getVisualVh: getVisualVh,
    refresh: refreshStableVh,
    onHeightTick: function (fn) {
      return subscribe(heightTickCallbacks, fn);
    },
    onGeometryRebuild: function (fn) {
      return subscribe(geometryRebuildCallbacks, fn);
    },
  };
})(window);
